import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "Science before story.",
    body: "Every formulation is built by understanding our customers' requirements together with doctors. We publish our actives, our dosages and our standards — because the clinicians who trust us demand nothing less.",
  },
  {
    n: "02",
    title: "One standard across two brands.",
    body: "Whether it's an oral therapeutic under OneKind or a barrier serum under One Skin, the same rigour applies: GMP manufacturing, clinical validation, and radical transparency.",
  },
  {
    n: "03",
    title: "Built for the doctor's shelf.",
    body: "We design for prescribers first. Clear evidence, honest claims, and a partnership model that puts your patients' outcomes at the centre of everything we make.",
  },
];

function Chapter({ chapter, i }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <div ref={ref} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start" data-testid={`manifesto-chapter-${chapter.n}`}>
      <motion.div style={{ y }} className="md:col-span-4">
        <span className="font-serif text-7xl md:text-9xl text-ink/15 leading-none">{chapter.n}</span>
      </motion.div>
      <div className="md:col-span-8 flex flex-col gap-5 md:pt-6">
        <h3 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight">
          <MaskedLines lines={[chapter.title]} />
        </h3>
        <Reveal delay={0.1}>
          <p className="font-sans text-base md:text-lg text-ink/65 leading-relaxed max-w-2xl">{chapter.body}</p>
        </Reveal>
      </div>
    </div>
  );
}

export default function Manifesto() {
  return (
    <section id="manifesto" className="py-28 md:py-40 px-6 md:px-12 scroll-mt-20">
      <div className="max-w-7xl mx-auto flex flex-col gap-24 md:gap-40">
        <Reveal>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">The Manifesto</span>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight mt-4 max-w-3xl leading-[0.95]">
            Healthcare, held to a higher standard.
          </h2>
        </Reveal>
        {CHAPTERS.map((c, i) => (
          <Chapter key={c.n} chapter={c} i={i} />
        ))}
      </div>
    </section>
  );
}
